import { HomeFilled } from "@ant-design/icons";
import { Empty, Form, Tabs } from "antd";
import omit from "lodash/omit";
import PropTypes from "prop-types";
import { useState } from "react";

import { schemaFormHasContent } from "@/entities/schema";
import { useFormValuesEffect } from "@/modules/form";
import { SchemaViewerField } from "@/modules/schema-viewer";
import { SchemaViewerFormProvider } from "@/modules/schema-viewer/schema-viewer-form";

import Field from "./Field";
import { FormParser } from "./FormParser";
import { getFormViewerTabGroup } from "./Group";

const FormViewer = ({
  schema,
  emptyDescription,
  formParams = {},
  datasetsImage,
  datasetsImageLoading,
  handleChange,
  disabled = false,
  strongLabel,
}) => {
  const evaluatedSchema = FormParser.parseConditions(schema);
  const form = formParams?.form;
  const [isInitialized, setIsInitialized] = useState(false);
  const { values: formValues } = useFormValuesEffect({
    form,
    onValuesChange: isInitialized ? handleChange : undefined,
  });

  /**
   * Evaluate if a field should be visible
   *
   * @return {bool}
   */
  const evaluateVisible = (field) => {
    return typeof field.visible === "function"
      ? field.visible(formValues)
      : true;
  };

  /**
   * Evaluate the values (select choices) for a field
   *
   * @return {array}
   */
  const evaluateValues = (field) => {
    return typeof field.values === "function"
      ? field.values(formValues)
      : field.values;
  };

  const fieldIsFilled = (model) => {
    const value = form?.getFieldValue(model);
    return value != null && value !== "";
  };

  /**
   * Returns the list of fields with their evaluated values
   *
   * @return {array}
   */
  const formattedFields = (fields) =>
    fields?.map((field) => {
      // remove non-DOM attributes
      const cleaned = omit(field, ["visible", "isFrozen"]);

      // Handle fields already migrated to modules
      if (["label", "input", "expression"].includes(field.type)) {
        return (
          <SchemaViewerField
            key={field.model}
            schema={field}
            form={formParams?.form}
            isFilled={fieldIsFilled(field.model)}
            isVisible={evaluateVisible(field)}
            disabled={disabled}
          />
        );
      }

      // Handle legacy fields (not migrated to modules yet)
      return (
        <Field
          schema={field}
          disabled={disabled}
          key={field.model}
          {...cleaned}
          values={evaluateValues(field)}
          value={form?.getFieldValue(field.model)}
          datasetsImage={datasetsImage}
          datasetsImageLoading={datasetsImageLoading}
          isFilled={fieldIsFilled(field.model)}
          isVisible={evaluateVisible(field)}
        />
      );
    });

  const UngroupedFields = () =>
    evaluatedSchema?.fields?.length
      ? getFormViewerTabGroup({
          key: "ungrouped",
          legend: <HomeFilled />,
          fields: formattedFields(evaluatedSchema?.fields),
          strongLabel,
        })
      : {};

  const Groups = () => {
    return evaluatedSchema?.groups
      ? evaluatedSchema.groups.map((group, key) => {
          return getFormViewerTabGroup({
            key: key,
            legend: group.legend,
            fields: formattedFields(group?.fields),
            strongLabel,
          });
        })
      : [];
  };

  return (
    <SchemaViewerFormProvider schema={evaluatedSchema}>
      <Form
        {...formParams}
        onValuesChange={() => setIsInitialized(true)}
        layout="vertical"
      >
        {schemaFormHasContent(evaluatedSchema) ? (
          <Tabs
            tabPlacement="left"
            className="form-viewer-tabs"
            items={[UngroupedFields(), ...Groups()]}
          />
        ) : (
          <Empty description={emptyDescription} />
        )}
      </Form>
    </SchemaViewerFormProvider>
  );
};

FormViewer.propTypes = {
  schema: PropTypes.object,
  emptyDescription: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  formParams: PropTypes.object,
  handleChange: PropTypes.func,
  disabled: PropTypes.bool,
  strongLabel: PropTypes.bool,
  datasetsImage: PropTypes.array,
  datasetsImageLoading: PropTypes.bool,
};

export default FormViewer;
